import React, { useState, useMemo } from 'react';
import { sans, mono, green, greenLight, greenDark, red, redLight, redDark, amber, amberLight, amberDark, useTheme } from '../tokens';
import { getDeadlineStatus } from '../helpers';
import { STATEMENT_TYPES, CATEGORIES, getPanelById } from '../data/panelCatalog';

const teal = '#0D9488';
const purple = '#7C3AED';
const purpleLight = '#EDE9FE';
const purpleDark = '#5B21B6';

// Panel status derivation from tasks
function getPanelStatus(tasks) {
  if (tasks.length === 0) return 'empty';
  const allReviewed = tasks.every(t => t.reviewed);
  const allDone = tasks.every(t => t.done || t.blocked);
  const anyDone = tasks.some(t => t.done);
  const anyBlocked = tasks.some(t => t.blocked);
  if (allReviewed) return 'reviewed';
  if (allDone) return 'ready'; // all prepared, awaiting review
  if (anyDone) return 'in-progress';
  if (anyBlocked) return 'blocked';
  return 'not-started';
}

const STATUS_CONFIG = {
  'reviewed':    { label: 'Reviewed',    bg: green + '14', color: green, border: green + '30', sortOrder: 5 },
  'ready':       { label: 'Ready for Review', bg: teal + '14', color: teal, border: teal + '30', sortOrder: 1 },
  'in-progress': { label: 'In Progress', bg: amber + '14', color: amber, border: amber + '30', sortOrder: 2 },
  'blocked':     { label: 'Blocked',     bg: purpleLight, color: purpleDark, border: purple + '30', sortOrder: 3 },
  'not-started': { label: 'Not Started', bg: '#F1F5F9', color: '#64748B', border: '#E2E8F0', sortOrder: 4 },
  'empty':       { label: 'No Tasks',    bg: '#F8FAFC', color: '#94A3B8', border: '#E2E8F0', sortOrder: 6 },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG['not-started'];
  return (
    <span style={{
      fontSize: 9, fontWeight: 600, fontFamily: sans,
      padding: '2px 8px', borderRadius: 10,
      background: cfg.bg, color: cfg.color,
      border: `1px solid ${cfg.border}`,
      whiteSpace: 'nowrap',
    }}>
      {cfg.label}
    </span>
  );
}

function ProgressBar({ tasks, height = 4 }) {
  const total = tasks.length;
  if (total === 0) return null;
  const reviewed = tasks.filter(t => t.reviewed).length;
  const done = tasks.filter(t => t.done && !t.reviewed).length;
  const blocked = tasks.filter(t => t.blocked).length;
  return (
    <div style={{ height, background: '#E2E8F0', borderRadius: height / 2, overflow: 'hidden', display: 'flex', flex: 1 }}>
      <div style={{ width: `${(reviewed / total) * 100}%`, background: green, transition: 'width 0.3s' }} />
      <div style={{ width: `${(done / total) * 100}%`, background: teal, transition: 'width 0.3s' }} />
      <div style={{ width: `${(blocked / total) * 100}%`, background: purple, transition: 'width 0.3s' }} />
    </div>
  );
}

// Review queue: tasks that are done but not yet reviewed
function ReviewQueue({ tasks, onToggleReview, onSelectPanel, mockToday, userInitials, t }) {
  const pendingReview = useMemo(() => {
    return tasks
      .filter(tk => tk.done && !tk.reviewed && !tk.blocked)
      .sort((a, b) => {
        // Sort by due date, then by section
        if (a.dueDate && b.dueDate) return a.dueDate.localeCompare(b.dueDate);
        return 0;
      });
  }, [tasks]);

  if (pendingReview.length === 0) {
    return (
      <div style={{
        textAlign: 'center', padding: '40px 20px',
        color: t.textFaint, fontSize: 12, fontStyle: 'italic',
      }}>
        No tasks awaiting review. All caught up.
      </div>
    );
  }

  // Group by section
  const grouped = {};
  pendingReview.forEach(tk => {
    const key = tk.section || 'Other';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(tk);
  });

  return (
    <div>
      {Object.entries(grouped).map(([section, sectionTasks]) => (
        <div key={section} style={{ marginBottom: 16 }}>
          <div style={{
            fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: 0.5, color: teal, fontFamily: sans,
            padding: '6px 0 4px',
            borderBottom: `2px solid ${teal}20`,
            marginBottom: 4,
          }}>
            {section} ({sectionTasks.length})
          </div>
          {sectionTasks.map(task => {
            const canReview = task.completedBy !== userInitials;
            const dlStatus = getDeadlineStatus(task.dueDate, task.done, mockToday);
            return (
              <div key={task.id} style={{
                display: 'grid',
                gridTemplateColumns: '1fr 70px 24px 80px',
                alignItems: 'center',
                padding: '7px 0',
                gap: 8,
                borderBottom: `1px solid ${t.borderLight || t.border}`,
              }}>
                <div>
                  <div style={{
                    fontSize: 12, fontFamily: sans, fontWeight: 500,
                    color: t.textPrimary,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {task.name}
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
                    <span style={{ fontSize: 9, fontFamily: mono, color: t.textFaint }}>
                      Prepared by {task.completedBy || '?'}
                    </span>
                    {task.completedAt && (
                      <span style={{ fontSize: 9, fontFamily: mono, color: t.textFaint }}>
                        {new Date(task.completedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    )}
                  </div>
                </div>
                <span style={{
                  fontSize: 10, fontFamily: mono, textAlign: 'right',
                  color: dlStatus === 'overdue' ? red : t.textFaint,
                }}>
                  {task.dueDate ? new Date(task.dueDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '--'}
                </span>
                <div style={{
                  width: 22, height: 22, borderRadius: '50%',
                  background: task.assignedTo ? teal + '20' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 8, fontWeight: 700, fontFamily: mono, color: teal,
                }}>
                  {task.assignedTo || ''}
                </div>
                <button
                  onClick={() => canReview && onToggleReview(task.id)}
                  disabled={!canReview}
                  style={{
                    border: `1px solid ${canReview ? green + '60' : t.border}`,
                    background: canReview ? green + '10' : 'transparent',
                    borderRadius: 6,
                    padding: '4px 10px', fontSize: 10, fontFamily: sans,
                    fontWeight: 600, cursor: canReview ? 'pointer' : 'not-allowed',
                    color: canReview ? greenDark : t.textFaint,
                    opacity: canReview ? 1 : 0.5,
                    transition: 'all 0.15s',
                  }}
                >
                  {canReview ? 'Approve' : 'Own work'}
                </button>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export default function ReviewDashboard({
  tasks,
  activePanelIds,
  onToggleReview,
  onSelectPanel,
  mockToday,
  userInitials,
}) {
  const t = useTheme();
  const [view, setView] = useState('panels'); // 'panels' or 'queue'
  const [expandedStatements, setExpandedStatements] = useState(
    new Set(STATEMENT_TYPES.map(st => st.id))
  );
  const [filterStatus, setFilterStatus] = useState('all');

  // Build panel data with task stats
  const panelData = useMemo(() => {
    const activeSet = new Set(activePanelIds || []);
    const tasksByPanel = {};

    tasks.forEach(tk => {
      // Use panelId if available, otherwise try legacy mapping
      const pId = tk.panelId;
      if (pId) {
        if (!tasksByPanel[pId]) tasksByPanel[pId] = [];
        tasksByPanel[pId].push(tk);
      }
    });

    // Also group by section via legacy mapping for tasks without panelId
    const { LEGACY_SECTION_MAP } = require('../data/panelCatalog');
    tasks.forEach(tk => {
      if (tk.panelId) return; // already mapped
      const mapped = LEGACY_SECTION_MAP[tk.section];
      if (mapped) {
        if (!tasksByPanel[mapped]) tasksByPanel[mapped] = [];
        tasksByPanel[mapped].push(tk);
      }
    });

    return Object.entries(tasksByPanel).map(([panelId, panelTasks]) => {
      const panel = getPanelById(panelId);
      if (!panel) return null;
      if (activeSet.size > 0 && !activeSet.has(panelId)) return null;
      const status = getPanelStatus(panelTasks);
      const total = panelTasks.length;
      const done = panelTasks.filter(tk => tk.done).length;
      const reviewed = panelTasks.filter(tk => tk.reviewed).length;
      const blocked = panelTasks.filter(tk => tk.blocked).length;
      const overdue = panelTasks.filter(tk => !tk.blocked && !tk.done && getDeadlineStatus(tk.dueDate, tk.done, mockToday) === 'overdue').length;
      return { panel, tasks: panelTasks, status, total, done, reviewed, blocked, overdue };
    }).filter(Boolean);
  }, [tasks, activePanelIds, mockToday]);

  // Summary stats
  const summary = useMemo(() => {
    const totalPanels = panelData.length;
    const reviewed = panelData.filter(p => p.status === 'reviewed').length;
    const ready = panelData.filter(p => p.status === 'ready').length;
    const inProgress = panelData.filter(p => p.status === 'in-progress').length;
    const blocked = panelData.filter(p => p.status === 'blocked').length;
    const totalTasks = tasks.length;
    const tasksDone = tasks.filter(tk => tk.done).length;
    const tasksReviewed = tasks.filter(tk => tk.reviewed).length;
    const tasksAwaitingReview = tasks.filter(tk => tk.done && !tk.reviewed).length;
    return { totalPanels, reviewed, ready, inProgress, blocked, totalTasks, tasksDone, tasksReviewed, tasksAwaitingReview };
  }, [panelData, tasks]);

  // Filter panels
  const filteredPanels = useMemo(() => {
    if (filterStatus === 'all') return panelData;
    return panelData.filter(p => p.status === filterStatus);
  }, [panelData, filterStatus]);

  // Group filtered panels by statement type
  const groupedPanels = useMemo(() => {
    const groups = {};
    for (const st of STATEMENT_TYPES) {
      const panels = filteredPanels
        .filter(p => p.panel.statementType === st.id)
        .sort((a, b) => {
          // Sort: ready-for-review first, then in-progress, then others
          const sa = STATUS_CONFIG[a.status]?.sortOrder || 99;
          const sb = STATUS_CONFIG[b.status]?.sortOrder || 99;
          return sa - sb;
        });
      if (panels.length > 0) {
        groups[st.id] = { label: st.label, panels };
      }
    }
    return groups;
  }, [filteredPanels]);

  const toggleStatement = (stId) => {
    setExpandedStatements(prev => {
      const next = new Set(prev);
      if (next.has(stId)) next.delete(stId);
      else next.add(stId);
      return next;
    });
  };

  return (
    <div>
      {/* Summary strip */}
      <div style={{
        display: 'flex', gap: 16, padding: '14px 18px',
        background: t.bgSurface || '#F8FAFC',
        border: `1px solid ${t.border}`, borderRadius: 10, marginBottom: 16,
        flexWrap: 'wrap',
      }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, fontFamily: mono, color: green }}>{summary.reviewed}</div>
          <div style={{ fontSize: 9, color: t.textFaint, textTransform: 'uppercase' }}>Panels Reviewed</div>
        </div>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, fontFamily: mono, color: teal }}>{summary.ready}</div>
          <div style={{ fontSize: 9, color: t.textFaint, textTransform: 'uppercase' }}>Ready for Review</div>
        </div>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, fontFamily: mono, color: amber }}>{summary.inProgress}</div>
          <div style={{ fontSize: 9, color: t.textFaint, textTransform: 'uppercase' }}>In Progress</div>
        </div>
        {summary.blocked > 0 && (
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, fontFamily: mono, color: purple }}>{summary.blocked}</div>
            <div style={{ fontSize: 9, color: t.textFaint, textTransform: 'uppercase' }}>Blocked</div>
          </div>
        )}
        <div style={{ borderLeft: `1px solid ${t.border}`, paddingLeft: 16 }}>
          <div style={{ fontSize: 22, fontWeight: 700, fontFamily: mono, color: t.textSecondary }}>{summary.totalPanels}</div>
          <div style={{ fontSize: 9, color: t.textFaint, textTransform: 'uppercase' }}>Total Panels</div>
        </div>
        <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
          <div style={{ fontSize: 22, fontWeight: 700, fontFamily: mono, color: green }}>
            {summary.tasksReviewed}<span style={{ fontSize: 13, color: t.textFaint, fontWeight: 400 }}>/{summary.totalTasks}</span>
          </div>
          <div style={{ fontSize: 9, color: t.textFaint, textTransform: 'uppercase' }}>Tasks Reviewed</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 22, fontWeight: 700, fontFamily: mono, color: teal }}>
            {summary.tasksAwaitingReview}
          </div>
          <div style={{ fontSize: 9, color: t.textFaint, textTransform: 'uppercase' }}>Awaiting Review</div>
        </div>
      </div>

      {/* Overall progress bar */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <ProgressBar tasks={tasks} height={6} />
          <span style={{ fontSize: 11, fontFamily: mono, color: t.textSecondary, whiteSpace: 'nowrap' }}>
            {summary.totalTasks > 0 ? Math.round((summary.tasksReviewed / summary.totalTasks) * 100) : 0}% reviewed
          </span>
        </div>
        <div style={{ display: 'flex', gap: 12, fontSize: 9, color: t.textFaint }}>
          <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: green, marginRight: 3, verticalAlign: 'middle' }} />Reviewed</span>
          <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: teal, marginRight: 3, verticalAlign: 'middle' }} />Prepared</span>
          <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: purple, marginRight: 3, verticalAlign: 'middle' }} />Blocked</span>
        </div>
      </div>

      {/* View toggle + filters */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 2, borderRadius: 6, overflow: 'hidden', border: `1px solid ${t.border}` }}>
          <button onClick={() => setView('panels')} style={{
            border: 'none', padding: '5px 12px', fontSize: 11, fontFamily: sans, cursor: 'pointer',
            background: view === 'panels' ? green + '14' : 'transparent',
            color: view === 'panels' ? green : t.textMuted, fontWeight: view === 'panels' ? 600 : 400,
          }}>Panels</button>
          <button onClick={() => setView('queue')} style={{
            border: 'none', padding: '5px 12px', fontSize: 11, fontFamily: sans, cursor: 'pointer',
            background: view === 'queue' ? green + '14' : 'transparent',
            color: view === 'queue' ? green : t.textMuted, fontWeight: view === 'queue' ? 600 : 400,
          }}>
            Review Queue
            {summary.tasksAwaitingReview > 0 && (
              <span style={{
                marginLeft: 4, fontSize: 9, fontWeight: 700,
                background: teal, color: '#fff', borderRadius: 8,
                padding: '1px 5px', verticalAlign: 'middle',
              }}>{summary.tasksAwaitingReview}</span>
            )}
          </button>
        </div>

        {view === 'panels' && (
          <div style={{ display: 'flex', gap: 3, marginLeft: 8 }}>
            {['all', 'ready', 'in-progress', 'reviewed', 'blocked', 'not-started'].map(s => {
              const label = s === 'all' ? 'All' : STATUS_CONFIG[s]?.label || s;
              const count = s === 'all' ? panelData.length : panelData.filter(p => p.status === s).length;
              if (s !== 'all' && count === 0) return null;
              return (
                <button key={s} onClick={() => setFilterStatus(s)} style={{
                  border: `1px solid ${filterStatus === s ? (STATUS_CONFIG[s]?.border || t.border) : t.border}`,
                  background: filterStatus === s ? (STATUS_CONFIG[s]?.bg || t.bgSurface) : 'transparent',
                  borderRadius: 12, padding: '3px 10px', fontSize: 10, fontFamily: sans,
                  cursor: 'pointer',
                  color: filterStatus === s ? (STATUS_CONFIG[s]?.color || t.textPrimary) : t.textMuted,
                  fontWeight: filterStatus === s ? 600 : 400,
                }}>
                  {label} ({count})
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Content */}
      {view === 'panels' ? (
        <div>
          {Object.entries(groupedPanels).map(([stId, group]) => {
            const isExpanded = expandedStatements.has(stId);
            const stTasks = group.panels.flatMap(p => p.tasks);
            const stReviewed = stTasks.filter(tk => tk.reviewed).length;
            const stTotal = stTasks.length;
            return (
              <div key={stId} style={{ marginBottom: 12 }}>
                {/* Statement type header */}
                <div
                  onClick={() => toggleStatement(stId)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
                    background: t.bgSurface || '#F8FAFC',
                    border: `1px solid ${t.border}`,
                    marginBottom: isExpanded ? 6 : 0,
                  }}
                >
                  <span style={{ fontSize: 10, color: t.textFaint, fontFamily: mono, width: 12 }}>
                    {isExpanded ? '\u25BC' : '\u25B6'}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 700, fontFamily: sans, color: t.textPrimary, flex: 1 }}>
                    {group.label}
                  </span>
                  <span style={{ fontSize: 10, fontFamily: mono, color: t.textFaint }}>
                    {stReviewed}/{stTotal} reviewed
                  </span>
                  <div style={{ width: 100 }}>
                    <ProgressBar tasks={stTasks} height={3} />
                  </div>
                  <span style={{ fontSize: 10, fontFamily: mono, color: t.textSecondary }}>
                    {group.panels.length} panels
                  </span>
                </div>

                {/* Panel rows */}
                {isExpanded && group.panels.map(pd => (
                  <div
                    key={pd.panel.id}
                    onClick={() => onSelectPanel && onSelectPanel(pd.panel.id)}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 120px 60px 60px 60px 100px',
                      alignItems: 'center',
                      padding: '8px 12px 8px 32px',
                      gap: 8,
                      borderBottom: `1px solid ${t.borderLight || t.border}`,
                      cursor: 'pointer',
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = t.bgSurface || '#F8FAFC'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 500, fontFamily: sans, color: t.textPrimary }}>
                        {pd.panel.label}
                      </div>
                      {pd.panel.glRange && (
                        <span style={{ fontSize: 9, fontFamily: mono, color: t.textFaint }}>
                          GL {pd.panel.glRange}
                        </span>
                      )}
                    </div>
                    <StatusBadge status={pd.status} />
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 13, fontWeight: 700, fontFamily: mono, color: teal }}>{pd.done}</div>
                      <div style={{ fontSize: 8, color: t.textFaint }}>Prep</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 13, fontWeight: 700, fontFamily: mono, color: green }}>{pd.reviewed}</div>
                      <div style={{ fontSize: 8, color: t.textFaint }}>Rev</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 13, fontWeight: 700, fontFamily: mono, color: t.textSecondary }}>{pd.total}</div>
                      <div style={{ fontSize: 8, color: t.textFaint }}>Total</div>
                    </div>
                    <ProgressBar tasks={pd.tasks} height={4} />
                  </div>
                ))}
              </div>
            );
          })}

          {filteredPanels.length === 0 && (
            <div style={{
              textAlign: 'center', padding: '40px 20px',
              color: t.textFaint, fontSize: 12, fontStyle: 'italic',
            }}>
              No panels match the selected filter.
            </div>
          )}
        </div>
      ) : (
        <ReviewQueue
          tasks={tasks}
          onToggleReview={onToggleReview}
          onSelectPanel={onSelectPanel}
          mockToday={mockToday}
          userInitials={userInitials}
          t={t}
        />
      )}
    </div>
  );
}
